import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { chW, chH } from "../../design/ui";
import { useApp } from "../../design/AppContext";
import { normalizeProject } from "../../design/domain";
import { EmptyState } from "../../components/mui";
import { apiPostForm } from "../../utils/api";

/* ===================== 常數（逐字取自 DESIGN L1310-L1327） ===================== */

/** 時間範圍下拉：label 照抄設計稿，value 為送後端的代碼 */
const RANGE_OPTIONS = [
  { v: "6w", label: "近 6 週" },
  { v: "quarter", label: "本季" },
  { v: "month", label: "本月" },
];

/**
 * 品質與流動指標的四張卡永遠要出現（設計稿是 2x2 固定版面），
 * 後端沒回資料時仍以這組定義撐住版面，避免格線塌掉。
 */
const QUALITY_DEFS = [
  { label: "審核通過率" },
  { label: "缺陷重開率" },
  { label: "缺陷密度", unit: "/kloc", raw: true },
  { label: "平均週期時間", unit: "天", raw: true },
];

/** 工項老化的四個級距（label 含 en-dash，與設計稿一致） */
const AGING_DEFS = [
  { label: "< 3 天", c: "#0e7c66" },
  { label: "3–7 天", c: "#2563eb" },
  { label: "7–14 天", c: "#dc6803" },
  { label: "> 14 天", c: "#d92d20" },
];

/** EVM 三條進度條的顏色與標籤（設計稿寫死的順序） */
const EVM_DEFS = [
  { key: "pv", label: "PV 計畫值", c: "#8a94a3" },
  { key: "ev", label: "EV 實獲值", c: "#0e7c66" },
  { key: "ac", label: "AC 實際成本", c: "#dc6803" },
];

/* ===================== 共用版面 ===================== */

const GRID_ROW = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 2 };
const SVG_SX = { width: "100%", height: "auto", display: "block" };

/** 圖表卡：標題＋可選的灰色副標 */
function ChartCard({ title, sub, children }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h3" component="h2" sx={{ mb: 1.75 }}>
          {title}
          {sub ? (
            <>
              {" "}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {sub}
              </Typography>
            </>
          ) : null}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

ChartCard.propTypes = { title: PropTypes.node, sub: PropTypes.node, children: PropTypes.node };
ChartCard.defaultProps = { title: null, sub: null, children: null };

/** 標籤＋數值＋一條色條（EVM 與工項老化共用） */
function BarRow({ label, value, width, color }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.625 }}>
        <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body2" component="span" sx={{ fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
      <Box sx={{ height: 8, bgcolor: "var(--line)", borderRadius: "var(--radius-pill)", overflow: "hidden" }}>
        <Box sx={{ width, height: "100%", bgcolor: color, borderRadius: "var(--radius-pill)" }} />
      </Box>
    </Box>
  );
}

BarRow.propTypes = { label: PropTypes.node, value: PropTypes.node, width: PropTypes.string, color: PropTypes.string };
BarRow.defaultProps = { label: null, value: null, width: "0%", color: "var(--accent)" };

/* ===================== 小工具 ===================== */

/** Math.max(...[]) 會回 -Infinity，統一從這裡取最大值避免座標算成 NaN */
function safeMax(arr) {
  const nums = (arr || []).map((n) => Number(n) || 0);
  return nums.length ? Math.max(...nums) : 0;
}

/** 後端可能回 snake_case 或 camelCase，逐一嘗試 */
function pick(obj, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const v = _.get(obj, keys[i]);
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

/** 取數字並保證不是 NaN */
function num(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

/** 不引入 file-saver，直接用 Blob + a[download] 觸發下載 */
function download(filename, content, mime) {
  try {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // 同步 revoke 在部分瀏覽器會讓下載中途被取消，等這一輪事件跑完再釋放
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  } catch (e) {
    return false;
  }
}

/** CSV 欄位跳脫 */
function csvCell(v) {
  const s = v === undefined || v === null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function App(props) {
  const {
    analytics: analyticsProp,
    projectList,
    projectMembers,
    GET_ReportAnalytics,
    GET_ProjectList,
    GET_ProjectMembers,
  } = props;
  const { toast } = useApp();
  const rootRef = useRef(null);
  /** PDF 抬頭的「產生時間」節點：匯出前直接寫進去，不繞 state（見 handleExportPdf） */
  const stampRef = useRef(null);

  /* 篩選條件：三個下拉都會觸發重新取數 */
  const [projectId, setProjectId] = useState("");
  const [range, setRange] = useState("6w");
  const [member, setMember] = useState("");

  useEffect(() => {
    GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
  }, [GET_ProjectList]);

  useEffect(() => {
    // 空值就不要送，免得後端把 projectId="" 當成有效篩選
    const payload = { range };
    if (projectId) payload.projectId = projectId;
    if (member) payload.member = member;
    GET_ReportAnalytics(payload, null, null);
  }, [GET_ReportAnalytics, projectId, range, member]);

  /* 專案切換時成員清單會變，順手把已選成員清掉避免送出無效組合 */
  useEffect(() => {
    if (projectId) GET_ProjectMembers({ ProjectId: projectId }, null, null);
  }, [GET_ProjectMembers, projectId]);

  /* ---------- 下拉選項 ---------- */
  const projects = useMemo(() => {
    const raw = Array.isArray(projectList) ? projectList : _.get(projectList, "data_list", []);
    return (raw || []).map(normalizeProject);
  }, [projectList]);

  // mapStateToProps 的預設值每次都是新物件，memo 起來下游 useMemo 才不會每次 render 都重算
  const analytics = useMemo(() => analyticsProp || {}, [analyticsProp]);

  /**
   * 成員下拉：value 用 account（與 /Performance 頁一致，後端 Project/Member/List 回的是
   * {account,name,...}），沒有 account 才退回名字。切回「全部專案」時 redux 內還留著上一個
   * 專案的成員，所以未選專案時不讀它，改用分析 API 附帶的成員清單。
   */
  const memberOptions = useMemo(() => {
    const toOpt = (m) => {
      if (typeof m === "string") return { v: m, label: m };
      const account = pick(m, "account", "Account") || "";
      const name = pick(m, "name", "Name", "member_name") || account;
      return { v: String(account || name || ""), label: String(name || account || "") };
    };
    if (projectId) {
      const raw = Array.isArray(projectMembers)
        ? projectMembers
        : _.get(projectMembers, "data_list", []);
      return _.uniqBy((raw || []).map(toOpt).filter((m) => m.v), "v");
    }
    const fromAnalytics = pick(analytics, "members", "Members") || [];
    return _.uniqBy(
      (Array.isArray(fromAnalytics) ? fromAnalytics : []).map(toOpt).filter((m) => m.v),
      "v"
    );
  }, [projectId, projectMembers, analytics]);

  /* ---------- ① 團隊速度 Velocity ---------- */
  const velocity = useMemo(() => {
    const raw = pick(analytics, "velocity", "Velocity") || [];
    return (Array.isArray(raw) ? raw : []).map((v) =>
      typeof v === "object" ? num(pick(v, "points", "value", "v")) : num(v)
    );
  }, [analytics]);

  const velBars = useMemo(() => {
    if (!velocity.length) return [];
    const velMax = safeMax(velocity) || 1; // 全 0 時避免除以 0
    return velocity.map((v, i) => ({
      x: i * (chW / velocity.length) + 8,
      w: chW / velocity.length - 16,
      h: (v / velMax) * (chH - 30),
      y: chH - 20 - (v / velMax) * (chH - 30),
      v,
      label: `S${i + 1}`,
    }));
  }, [velocity]);

  /* ---------- ② 燃盡圖 Burndown ---------- */
  const burndown = useMemo(() => {
    const ideal = pick(analytics, "burndownIdeal", "burndown_ideal") || [];
    const actual = pick(analytics, "burndownActual", "burndown_actual") || [];
    return {
      ideal: (Array.isArray(ideal) ? ideal : []).map((v) => num(v)),
      actual: (Array.isArray(actual) ? actual : []).map((v) => num(v)),
    };
  }, [analytics]);

  const bd = useMemo(() => {
    const bdN = burndown.ideal.length;
    if (bdN < 2) return { ideal: "", actual: "", ok: false }; // 只有 1 點時 chW/(bdN-1) 會變 Infinity
    // 設計稿寫死 v/80（種子資料的初始 scope）；真實 scope 不會剛好是 80，改用實際最大值縮放，
    // 否則超過 80 的點會畫到畫布外。沒資料時仍退回 80，畫出來與設計稿一致。
    const bdMax = safeMax([...burndown.ideal, ...burndown.actual]) || 80;
    const bdX = (i) => i * (chW / (bdN - 1));
    const bdY = (v) => chH - 20 - (v / bdMax) * (chH - 30);
    return {
      ideal: burndown.ideal.map((v, i) => `${bdX(i).toFixed(0)},${bdY(v).toFixed(0)}`).join(" "),
      actual: burndown.actual.map((v, i) => `${bdX(i).toFixed(0)},${bdY(v).toFixed(0)}`).join(" "),
      ok: true,
    };
  }, [burndown]);

  /* ---------- ③ 實獲值分析 EVM ---------- */
  const evm = useMemo(() => {
    const e = pick(analytics, "evm", "EVM") || {};
    return {
      pv: num(pick(e, "pv", "PV")),
      ev: num(pick(e, "ev", "EV")),
      ac: num(pick(e, "ac", "AC")),
      spi: num(pick(e, "spi", "SPI")),
      cpi: num(pick(e, "cpi", "CPI")),
    };
  }, [analytics]);

  const evmBars = useMemo(
    () => EVM_DEFS.map((b) => ({ ...b, v: evm[b.key], barW: `${evm[b.key]}%` })),
    [evm]
  );

  const evmKpis = useMemo(
    () => [
      { label: "SPI 進度績效", val: evm.spi.toFixed(2), c: evm.spi >= 1 ? "#0e7c66" : "#dc6803" },
      { label: "CPI 成本績效", val: evm.cpi.toFixed(2), c: evm.cpi >= 1 ? "#0e7c66" : "#d92d20" },
    ],
    [evm]
  );

  /* ---------- ④ 品質與流動指標 ---------- */
  const quality = useMemo(() => {
    const raw = pick(analytics, "quality", "Quality");
    if (Array.isArray(raw) && raw.length) {
      return raw.map((q, i) => {
        const val = pick(q, "val", "Val", "value", "Value");
        const unit = pick(q, "unit", "Unit") || "";
        const isRaw = !!pick(q, "raw", "Raw");
        return {
          label: pick(q, "label", "Label") || _.get(QUALITY_DEFS, [i, "label"], ""),
          // 後端欄位缺漏時顯示「—」，不要印出 "undefined%"
          val: val === undefined ? "—" : `${val}${isRaw ? unit : "%"}`,
        };
      });
    }
    return QUALITY_DEFS.map((d) => ({ label: d.label, val: "—" }));
  }, [analytics]);

  const throughput = useMemo(() => {
    const raw = pick(analytics, "throughput", "Throughput") || [];
    return (Array.isArray(raw) ? raw : []).map((v) =>
      typeof v === "object" ? num(pick(v, "count", "value", "v")) : num(v)
    );
  }, [analytics]);

  const thBars = useMemo(() => {
    if (!throughput.length) return [];
    const thMax = safeMax(throughput) || 1;
    return throughput.map((v, i) => ({
      x: i * (chW / throughput.length) + 8,
      w: chW / throughput.length - 16,
      h: (v / thMax) * (chH - 30),
      y: chH - 20 - (v / thMax) * (chH - 30),
      v,
      label: `W${i + 1}`,
    }));
  }, [throughput]);

  /* ---------- ⑤ 累積流量圖 CFD ---------- */
  const cfd = useMemo(() => {
    const raw = pick(analytics, "cfd", "CFD") || [];
    return (Array.isArray(raw) ? raw : []).map((c, i) => ({
      w: pick(c, "w", "week", "label") || `W${i + 1}`,
      todo: num(pick(c, "todo", "pending")),
      prog: num(pick(c, "prog", "progress", "doing")),
      done: num(pick(c, "done", "completed")),
    }));
  }, [analytics]);

  const cfdBars = useMemo(() => {
    if (!cfd.length) return [];
    const cfdMax = safeMax(cfd.map((c) => c.todo + c.prog + c.done)) || 1;
    const cfdSc = (chH - 30) / cfdMax;
    return cfd.map((c, i) => {
      const bw = chW / cfd.length - 14;
      const x = i * (chW / cfd.length) + 7;
      const base = chH - 20;
      const dH = c.done * cfdSc;
      const pH = c.prog * cfdSc;
      const tH = c.todo * cfdSc;
      return {
        x,
        bw,
        dY: base - dH,
        dH,
        pY: base - dH - pH,
        pH,
        tY: base - dH - pH - tH,
        tH,
        label: c.w,
      };
    });
  }, [cfd]);

  /* ---------- ⑥ 工項老化 ---------- */
  const aging = useMemo(() => {
    const raw = pick(analytics, "aging", "ageing") || [];
    const list = Array.isArray(raw) ? raw : [];
    return AGING_DEFS.map((d, i) => {
      const r = list[i];
      const n =
        typeof r === "number" ? num(r) : num(pick(r || {}, "n", "count", "value"));
      return {
        label: (r && pick(r, "label", "Label")) || d.label,
        n,
        c: (r && pick(r, "c", "color")) || d.c,
      };
    });
  }, [analytics]);

  const agBars = useMemo(() => {
    const agMax = safeMax(aging.map((a) => a.n)) || 1;
    return aging.map((a) => ({ ...a, w: `${Math.round((a.n / agMax) * 100)}%` }));
  }, [aging]);

  /* ---------- 匯出 ---------- */

  /* PDF 抬頭要寫的篩選條件（畫面上的下拉不會被拍進去，見 data-pdf-only） */
  const pdfProjectLabel =
    (projects.find((p) => String(p.id) === String(projectId)) || {}).name || "全部專案";
  const pdfRangeLabel =
    (RANGE_OPTIONS.find((r) => String(r.v) === String(range)) || {}).label || "—";
  const pdfMemberLabel =
    (memberOptions.find((m) => String(m.v) === String(member)) || {}).label || "全部成員";

  // html2canvas 只在按下匯出時才載入，避免拖慢首屏；失敗就退回瀏覽器列印
  const handleExportPdf = async () => {
    try {
      /*
       * 產生時間直接寫進 DOM 而不是走 state：
       * setState 之後要等重新渲染才拿得到新的節點，中間去 await 匯出會拍到舊值。
       */
      if (stampRef.current) {
        stampRef.current.textContent = `產生時間：${new Date().toLocaleString("zh-TW", { hour12: false })}`;
      }
      // 匯出期間畫面會暫時切成淺色、字級歸成標準字（見 utils/exportPdf），
      // 先講一聲才不會被誤以為畫面壞了
      toast("正在產生 PDF…");
      const mod = await import("../../utils/exportPdf");
      const stamp = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const name = `報表與分析_${stamp.getFullYear()}${pad(stamp.getMonth() + 1)}${pad(stamp.getDate())}.pdf`;
      await mod.exportElementToPdf(rootRef.current, name);
      toast("已匯出 PDF");
    } catch (e) {
      toast("PDF 產生失敗，已改用瀏覽器列印", "error");
      window.print();
    }
  };

  const handleExportExcel = () => {
    const rows = [];
    rows.push(["區塊", "項目", "數值"]);
    velocity.forEach((v, i) => rows.push(["團隊速度 Velocity", `S${i + 1}`, v]));
    burndown.ideal.forEach((v, i) => rows.push(["燃盡圖 計畫", `D${i + 1}`, v]));
    burndown.actual.forEach((v, i) => rows.push(["燃盡圖 實際", `D${i + 1}`, v]));
    evmBars.forEach((b) => rows.push(["實獲值分析 EVM", b.label, b.v]));
    evmKpis.forEach((k) => rows.push(["實獲值分析 EVM", k.label, k.val]));
    quality.forEach((q) => rows.push(["品質與流動指標", q.label, q.val]));
    throughput.forEach((v, i) => rows.push(["每週產出 Throughput", `W${i + 1}`, v]));
    cfd.forEach((c) => {
      rows.push(["累積流量圖 CFD", `${c.w} 待處理`, c.todo]);
      rows.push(["累積流量圖 CFD", `${c.w} 進行中`, c.prog]);
      rows.push(["累積流量圖 CFD", `${c.w} 已完成`, c.done]);
    });
    aging.forEach((a) => rows.push(["工項老化", a.label, a.n]));
    // 加 BOM 讓 Excel 正確辨識 UTF-8 中文（同 Audit 頁的寫法，不在原始碼裡藏看不見的字元）
    const csv =
      String.fromCharCode(0xfeff) + rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
    const ok = download("報表與分析.csv", csv, "text/csv;charset=utf-8;");
    toast(ok ? "已匯出 Excel" : "匯出失敗", ok ? undefined : "error");
  };

  /*
   * 排程寄送是這一頁唯一會寫資料的動作，原本完全沒有送出中的狀態：
   * 按下去畫面毫無變化，慢一點使用者就會連按，等於重複送出同一份排程設定。
   * 匯出 PDF／Excel 是純前端運算，不需要這層防護。
   */
  const [scheduling, setScheduling] = useState(false);

  const handleSchedule = async () => {
    if (scheduling) return;
    setScheduling(true);
    try {
      await apiPostForm("/Report/Schedule", { projectId, range, member });
      toast("已設定排程寄送");
    } catch (e) {
      // 後端排程 API 尚未完成，先明確告知使用者而不是靜默失敗
      toast("排程寄送尚未開放", "error");
    } finally {
      setScheduling(false);
    }
  };

  /* ===================== render ===================== */

  return (
    <Box ref={rootRef} className="pg" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/*
        只在匯出的 PDF 裡出現（見 utils/exportPdf 的 data-pdf-only）。
        畫面上的篩選列不會被拍進 PDF，不另外寫一份摘要的話，
        印出來沒人知道這份報表是哪個專案、哪段期間、什麼時候產的。
        平常的 display:none 寫在 sx（class）裡，匯出時 exportPdf 用 inline style 蓋成 block。
      */}
      <Box data-pdf-only sx={{ display: "none", pt: 0.5, px: 0.25, pb: 1.25, borderBottom: "2px solid var(--accent)" }}>
        <Typography variant="h2" component="div" sx={{ fontWeight: 900 }}>
          報表與分析
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.75 }}>
          {`專案：${pdfProjectLabel}　·　期間：${pdfRangeLabel}　·　成員：${pdfMemberLabel}`}
        </Typography>
        <Typography ref={stampRef} variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.375 }} />
      </Box>

      {/* 篩選列：控制項只在畫面上有意義，匯出時整個拿掉 */}
      <Card data-pdf-hide>
        <CardContent sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", px: 2, py: 1.75, "&:last-child": { pb: 1.75 } }}>
          <TextField
            select
            slotProps={{ select: { native: true }, htmlInput: { "aria-label": "專案" } }}
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              setMember("");
            }}
          >
            <option value="">全部專案</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            slotProps={{ select: { native: true }, htmlInput: { "aria-label": "時間範圍" } }}
            value={range}
            onChange={(e) => setRange(e.target.value)}
          >
            {RANGE_OPTIONS.map((r) => (
              <option key={r.v} value={r.v}>
                {r.label}
              </option>
            ))}
          </TextField>
          <TextField
            select
            slotProps={{ select: { native: true }, htmlInput: { "aria-label": "成員" } }}
            value={member}
            onChange={(e) => setMember(e.target.value)}
          >
            <option value="">全部成員</option>
            {memberOptions.map((m) => (
              <option key={m.v} value={m.v}>
                {m.label}
              </option>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" component="span" color="text.secondary" sx={{ fontWeight: 600 }}>
            配色採色盲友善
          </Typography>
        </CardContent>
      </Card>

      {/* ①② 團隊速度 / 燃盡圖 */}
      <Box sx={GRID_ROW}>
        <ChartCard title="團隊速度 Velocity">
          {velBars.length ? (
            <Box component="svg" viewBox={`0 0 ${chW} ${chH}`} sx={SVG_SX}>
              {velBars.map((b, i) => (
                <rect
                  key={`vel-${b.label}-${i}`}
                  x={b.x}
                  y={b.y}
                  width={b.w}
                  height={b.h}
                  rx="3"
                  fill="var(--accent)"
                />
              ))}
            </Box>
          ) : (
            <EmptyState dense text="尚無資料" />
          )}
        </ChartCard>
        <ChartCard title="燃盡圖 Burndown" sub="計畫 vs 實際">
          {bd.ok ? (
            <Box component="svg" viewBox={`0 0 ${chW} ${chH}`} sx={SVG_SX}>
              <polyline
                points={bd.ideal}
                fill="none"
                stroke="var(--faint)"
                strokeWidth="2"
                strokeDasharray="5 5"
              />
              <polyline points={bd.actual} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
            </Box>
          ) : (
            <EmptyState dense text="尚無資料" />
          )}
        </ChartCard>
      </Box>

      {/* ③④ 實獲值分析 / 品質與流動指標 */}
      <Box sx={GRID_ROW}>
        <ChartCard title="實獲值分析 EVM">
          {evmBars.map((b) => (
            <BarRow key={b.key} label={b.label} value={b.v} width={b.barW} color={b.c} />
          ))}
          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
            {evmKpis.map((k) => (
              <Box key={k.label} sx={{ flex: 1, bgcolor: "action.hover", borderRadius: "var(--radius-sm)", px: 1.75, py: 1.5 }}>
                <Typography variant="h2" component="div" sx={{ fontWeight: 900, color: k.c }}>
                  {k.val}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.25 }}>
                  {k.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </ChartCard>

        <ChartCard title="品質與流動指標">
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            {quality.map((q, i) => (
              <Box key={`q-${q.label}-${i}`} sx={{ bgcolor: "action.hover", borderRadius: "var(--radius-sm)", p: 1.75 }}>
                <Typography variant="h2" component="div" sx={{ fontWeight: 900 }}>
                  {q.val}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.375 }}>
                  {q.label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="h3" component="h3" sx={{ mt: 2.25, mb: 1.5 }}>
            每週產出 Throughput
          </Typography>
          {thBars.length ? (
            <Box component="svg" viewBox={`0 0 ${chW} ${chH}`} sx={SVG_SX}>
              {thBars.map((b, i) => (
                <rect
                  key={`th-${b.label}-${i}`}
                  x={b.x}
                  y={b.y}
                  width={b.w}
                  height={b.h}
                  rx="3"
                  fill="#2563eb"
                />
              ))}
            </Box>
          ) : (
            <EmptyState dense text="尚無資料" />
          )}
        </ChartCard>
      </Box>

      {/* ⑤⑥ 累積流量圖 / 工項老化 */}
      <Box sx={GRID_ROW}>
        <ChartCard title="累積流量圖 CFD">
          {cfdBars.length ? (
            <Box component="svg" viewBox={`0 0 ${chW} ${chH}`} sx={SVG_SX}>
              {cfdBars.map((b, i) => (
                <React.Fragment key={`cfd-${b.label}-${i}`}>
                  <rect x={b.x} y={b.tY} width={b.bw} height={b.tH} fill="#8a94a3" />
                  <rect x={b.x} y={b.pY} width={b.bw} height={b.pH} fill="#2563eb" />
                  <rect x={b.x} y={b.dY} width={b.bw} height={b.dH} fill="#0e7c66" />
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <EmptyState dense text="尚無資料" />
          )}
          <Stack direction="row" spacing={1.75} sx={{ mt: 1 }}>
            {[
              ["#8a94a3", "待處理"],
              ["#2563eb", "進行中"],
              ["#0e7c66", "已完成"],
            ].map(([c, label]) => (
              <Typography
                key={label}
                variant="body2"
                component="span"
                color="text.secondary"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.625, fontWeight: 600 }}
              >
                <Box component="span" sx={{ width: 10, height: 10, bgcolor: c }} />
                {label}
              </Typography>
            ))}
          </Stack>
        </ChartCard>

        <ChartCard title="工項老化" sub="未完成工項停留時間">
          {agBars.map((a, i) => (
            <BarRow key={`ag-${a.label}-${i}`} label={a.label} value={a.n} width={a.w} color={a.c} />
          ))}
        </ChartCard>
      </Box>

      {/* 匯出：這排按鈕自己不該出現在報表裡 */}
      <Stack data-pdf-hide direction="row" spacing={1.25} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Button variant="outlined" onClick={handleExportPdf}>
          匯出 PDF
        </Button>
        <Button variant="outlined" onClick={handleExportExcel}>
          匯出 Excel
        </Button>
        <Button
          variant="outlined"
          disabled={scheduling}
          loading={scheduling}
          loadingPosition="start"
          onClick={handleSchedule}
        >
          {scheduling ? "設定中…" : "排程寄送"}
        </Button>
      </Stack>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  analytics: _.get(state, "Report.Analytics", {}),
  projectList: _.get(state, "Project.List", {}),
  projectMembers: _.get(state, "Project.Members", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ReportAnalytics: (payload, callback, loading) =>
    dispatch({ type: "GET_ReportAnalytics", payload, callback, loading }),
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
  GET_ProjectMembers: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectMembers", payload, callback, loading }),
});

App.propTypes = {
  analytics: PropTypes.object,
  projectList: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  projectMembers: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  GET_ReportAnalytics: PropTypes.func,
  GET_ProjectList: PropTypes.func,
  GET_ProjectMembers: PropTypes.func,
};

App.defaultProps = {
  analytics: {},
  projectList: {},
  projectMembers: [],
  GET_ReportAnalytics: () => {},
  GET_ProjectList: () => {},
  GET_ProjectMembers: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
